import { useRef } from 'react';
import { Link } from 'react-router';
import { Reveal } from '@components/motion';
import { onImageError } from '@/lib/imageFallback';
import { STORIES } from '@/data/resources';
import { SOCIAL_URLS } from '@/data/navigation';
import FigmaFrame from './FigmaFrame';
import { Img } from '@/components/Img';

/** How many cards the carousel carries. */
const CARD_COUNT = 6;

/**
 * The shared story pool, hydrated from the CMS before first render (see
 * `hydrateSiteContent`), so this rail, the stories index and the story pages all
 * show the same published stories. Falls back to bundled copy if the feed is down.
 */
const carouselStories = () => STORIES.slice(0, CARD_COUNT);

const ELLIPSE_LG = '/landing/stories/ellipse-lg.svg';
const ELLIPSE_SM = '/landing/stories/ellipse-sm.svg';
const VIEW_ALL_CIRCLE = '/landing/stories/view-all-circle.svg';

const SOCIALS = [
    { icon: '/landing/stories/twitter.svg', label: 'X', network: 'x' },
    { icon: '/landing/stories/facebook.svg', label: 'Facebook', network: 'facebook' },
    { icon: '/landing/stories/instagram.svg', label: 'Instagram', network: 'instagram' },
    { icon: '/landing/stories/youtube.svg', label: 'YouTube', network: 'youtube' },
];

const INTRO_PARAGRAPH =
    'Behind every case resolved, every right protected, and every voice heard is a person who finally found a way forward. These stories capture what happens when justice becomes accessible: fear gives way to clarity, silence turns into action, and individuals regain trust in the system meant to protect them.';

function StoriesDesktop() {
    const stories = carouselStories();
    const trackRef = useRef<HTMLDivElement>(null);
    // Scroll the carousel by one card (card width 455 + gap 39).
    const scrollCards = (dir: number) => {
        trackRef.current?.scrollBy({ left: dir * 494, behavior: 'smooth' });
    };

    return (
        <FigmaFrame designWidth={1920} designHeight={1006} className="w-full">
            <div className="relative h-[1006px] w-[1920px] overflow-hidden bg-[#f9f9f9] text-[#333]">
                {/* Decorative background ellipses */}
                <Img
                    src={ELLIPSE_LG}
                    alt=""
                    className="pointer-events-none absolute left-[166px] top-[-75px] size-[1334px]"
                />
                <Img
                    src={ELLIPSE_SM}
                    alt=""
                    className="pointer-events-none absolute left-[487px] top-[246px] size-[692px]"
                />

                {/* Eyebrow */}
                <p className="absolute left-[127px] top-[56px] w-[290px] text-[18px] font-normal leading-[28px] text-[#333] opacity-[0.39]">
                    Numbers show scale — but stories show meaning.
                </p>

                {/* Heading (two-tone) — flows inline so it wraps as designed:
                    "The Stories / Behind the / Numbers" */}
                <Reveal
                    as="h2"
                    once
                    variant="fade-up"
                    className="absolute left-[127px] top-[150px] w-[644px] text-[96px] font-extrabold leading-[103px] text-[#1b1b1b]"
                >
                    <span>The Stories Behind </span>
                    <span className="font-normal text-[#6c6c6c]">the Numbers</span>
                </Reveal>

                {/* Intro paragraph */}
                <Reveal
                    as="p"
                    once
                    variant="fade-up"
                    delay={160}
                    className="absolute left-[127px] top-[490px] w-[420px] text-[16px] font-normal leading-[24px] text-[#333]"
                >
                    {INTRO_PARAGRAPH}
                </Reveal>

                {/* View All Stories button */}
                <Link
                    to="/resources/stories"
                    className="absolute left-[127px] top-[721px] flex w-[216px] items-center justify-center rounded-[10px] bg-[#f60] px-[20px] py-[10px] text-[16px] font-bold leading-[24px] text-white"
                >
                    View All Stories
                </Link>

                {/* Social icons */}
                <div className="absolute left-[136px] top-[818px] flex flex-col gap-[27px]">
                    {SOCIALS.map((s) =>
                        SOCIAL_URLS[s.network] ? (
                            <a
                                key={s.label}
                                href={SOCIAL_URLS[s.network]}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={s.label}
                            >
                                <Img src={s.icon} alt="" className="size-[19px]" />
                            </a>
                        ) : null,
                    )}
                </div>

                {/* View All (top-right) */}
                <Reveal as="div" once variant="scale" delay={240} className="absolute left-[1533px] top-[227px]">
                    <Link to="/resources/stories" className="flex items-center gap-[17px]">
                        <span className="relative flex size-[124px] items-center justify-center">
                            <Img src={VIEW_ALL_CIRCLE} alt="" className="absolute inset-0 size-full" />
                            <svg className="relative size-[56px]" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                                <path
                                    d="M9 23L23 9"
                                    stroke="white"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M10 9H23V22"
                                    stroke="white"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span className="whitespace-nowrap text-[24px] font-bold leading-[32px] text-[#1b1b1b]">
                            View All
                        </span>
                    </Link>
                </Reveal>

                {/* Story cards carousel — every card carries its caption beneath the
                    image. Scrolled by the arrows below. */}
                <div
                    ref={trackRef}
                    className="scrollbar-hide absolute left-[695px] top-[418px] flex h-[398px] w-[1225px] items-start gap-[39px] overflow-x-auto scroll-smooth"
                >
                    {stories.map((story) => (
                        <div key={story.slug} className="flex w-[455px] shrink-0 flex-col gap-[22px]">
                            <Link
                                to={`/resources/stories/${story.slug}`}
                                aria-label={`Read: ${story.title}`}
                                className="group relative block h-[283px] w-full overflow-hidden shadow-[0_20px_44px_-18px_rgba(0,0,0,0.28)]"
                            >
                                <Img
                                    src={story.image}
                                    alt=""
                                    onError={onImageError(story.fallbackImage)}
                                    className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                                />
                                <span className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/25" />
                                {/* Sits on the image, as designed — visible at rest, lifts on hover. */}
                                <span className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-[10px] rounded-[12px] border border-white/70 bg-black/35 px-[26px] py-[13px] text-[18px] font-bold leading-[24px] text-white backdrop-blur-[2px] transition-transform duration-300 group-hover:scale-[1.06]">
                                    View
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path
                                            d="M7 17L17 7M17 7H8M17 7V16"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            </Link>
                            <p className="line-clamp-3 text-[18px] font-bold leading-[28px] text-[#333]">
                                {story.excerpt || story.title}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Carousel arrows — circular, aligned to the first image's left edge */}
                <div className="absolute left-[695px] top-[822px] flex items-center gap-[16px]">
                    <button
                        type="button"
                        aria-label="Previous"
                        onClick={() => scrollCards(-1)}
                        className="flex size-[46px] items-center justify-center rounded-full bg-[#ececec] text-[#1b1b1b] transition-colors hover:bg-[#e0e0e0]"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                                d="M15 6l-6 6 6 6"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                    <button
                        type="button"
                        aria-label="Next"
                        onClick={() => scrollCards(1)}
                        className="flex size-[46px] items-center justify-center rounded-full bg-[#ececec] text-[#1b1b1b] transition-colors hover:bg-[#e0e0e0]"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                                d="M9 6l6 6-6 6"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                {/* Right-aligned descriptor */}
                <p className="absolute left-[1580px] top-[828px] w-[203px] text-right text-[18px] font-bold leading-[25px] text-[#333]">
                    How access to justice changed real lives, one case at a time.
                </p>
            </div>
        </FigmaFrame>
    );
}

function CarouselArrow({
    direction,
    label,
    onClick,
}: {
    direction: 'prev' | 'next';
    label: string;
    onClick?: () => void;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            onClick={onClick}
            className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-[#e6e6e6] text-[#1b1b1b]"
        >
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                className={direction === 'prev' ? 'rotate-180' : ''}
            >
                <path
                    d="M9 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    );
}

function StoriesMobile() {
    const stories = carouselStories();
    const trackRef = useRef<HTMLDivElement>(null);
    const scrollCards = (dir: number) => {
        trackRef.current?.scrollBy({ left: dir * 246, behavior: 'smooth' });
    };
    return (
        <section className="bg-[#f9f9f9] px-5 pb-12 pt-12 text-[#333]">
            <p className="w-[290px] text-[12px] font-normal leading-[20px] text-[#333] opacity-[0.39]">
                Numbers show scale — but stories show meaning.
            </p>

            <h2 className="mt-3 font-extrabold leading-[50px] text-[#1b1b1b]">
                <span className="text-[40px]">The Stories Behind </span>
                <span className="text-[40px] font-normal text-[#6c6c6c]">the Numbers</span>
            </h2>

            <p className="mt-5 text-[14px] font-normal leading-[24px] text-[#333]">{INTRO_PARAGRAPH}</p>

            {/* Photo carousel — each image carries its caption beneath it */}
            <div className="mt-7">
                <div ref={trackRef} className="scrollbar-hide -mx-5 flex gap-[24px] overflow-x-auto scroll-smooth px-5">
                    {stories.map((story) => (
                        <div key={story.slug} className="flex w-[207px] shrink-0 flex-col gap-[14px]">
                            <Link
                                to={`/resources/stories/${story.slug}`}
                                aria-label={`Read: ${story.title}`}
                                className="relative block h-[129px] w-full overflow-hidden shadow-[0_14px_30px_-8px_rgba(0,0,0,0.22)]"
                            >
                                <Img
                                    src={story.image}
                                    alt=""
                                    onError={onImageError(story.fallbackImage)}
                                    className="absolute inset-0 size-full object-cover"
                                />
                                <span className="absolute inset-0 bg-black/15" />
                                <span className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-[6px] rounded-[9px] border border-white/70 bg-black/35 px-[16px] py-[7px] text-[13px] font-bold leading-[18px] text-white">
                                    View
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path
                                            d="M7 17L17 7M17 7H8M17 7V16"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            </Link>
                            <p className="line-clamp-4 text-[13px] font-bold leading-[18px] text-[#333]">
                                {story.excerpt || story.title}
                            </p>
                        </div>
                    ))}
                </div>

                <div className="mb-2 mt-6 flex items-center gap-[18px]">
                    <CarouselArrow direction="prev" label="Previous story" onClick={() => scrollCards(-1)} />
                    <CarouselArrow direction="next" label="Next story" onClick={() => scrollCards(1)} />
                </div>
            </div>

            <Link
                to="/resources/stories"
                className="mt-7 flex w-full items-center justify-center rounded-[10px] bg-[#1b1b1b] px-5 py-[10px] text-[16px] font-bold leading-[24px] text-white"
            >
                View All Stories
            </Link>
        </section>
    );
}

export default function Stories() {
    return (
        <>
            <div className="hidden lg:block">
                <StoriesDesktop />
            </div>
            <div className="lg:hidden">
                <StoriesMobile />
            </div>
        </>
    );
}
